import cx from "classnames";
import React, { ReactNode } from "react";

import {
  Column,
  Container,
  Row,
  useCurrentViewPort,
  ViewPort,
} from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { SeparatorBar } from "../";
import { useProgramColorPicker } from "../../helpers/useProgramColorPicker";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import {
  AddIcon,
  ArrowsLeftRightIcon,
  CheckMarkIcon,
  CircleIcon,
  LeftArrowIcon,
  LockIcon,
  TimerIcon,
} from "../icons";
import * as styles from "./ContentBox.module.css";

export enum ContentBoxIcon {
  Lock = "Lock",
  CheckMark = "CheckMark",
  BlueCheckMark = "BlueCheckMark",
  GreenCircle = "GreenCircle",
  Timer = "TimerIcon",
  LeftArrow = "LeftArrow",
  Add = "Add",
  ArrowsLeftRightSwap = "ArrowsLeftRight",
}

export type ContentBoxRightSideLink = {
  text: string;
  onClick?: () => void;
  href?: string;
  icon?: ContentBoxIcon;
};
export type ContentBoxRightSideButton = {
  text: string;
  onClick: () => void;
  href?: string;
  icon?: ContentBoxIcon;
};

export type ContentBoxTitle = {
  title: string;
  subtitle?: string;
  icon?: ContentBoxIcon;
  rightSideText?: string;
  disabled?: boolean;
  separatorBar?: boolean;
  rightSideLink?: ContentBoxRightSideLink;
  rightSideButton?: ContentBoxRightSideButton;
};

export type ContentBoxProps = Readonly<{
  className?: string;
  children?: ReactNode;
  titleConfiguration?: ContentBoxTitle;
}>;

const getIcon = (icon: ContentBoxIcon): ReactNode => {
  switch (icon) {
    case ContentBoxIcon.Lock:
      return <LockIcon />;
    case ContentBoxIcon.CheckMark:
      return <CheckMarkIcon />;
    case ContentBoxIcon.GreenCircle:
      return <CircleIcon />;
    case ContentBoxIcon.Timer:
      return <TimerIcon size="S" />;
    case ContentBoxIcon.LeftArrow:
      return <LeftArrowIcon />;
    case ContentBoxIcon.Add:
      return <AddIcon />;
    case ContentBoxIcon.ArrowsLeftRightSwap:
      return <ArrowsLeftRightIcon />;
    default:
      return null;
  }
};

type RightSideProps = Readonly<{
  className?: string;
  children?: ReactNode;
  titleConfiguration?: ContentBoxTitle;
}>;

const RightSide = ({ titleConfiguration }: RightSideProps) => {
  const colorStyle = useProgramColorPicker();
  if (!titleConfiguration) {
    return null;
  }
  const rightSideLinkConfig = titleConfiguration?.rightSideLink;
  const rightSideButtonConfig = titleConfiguration?.rightSideButton;

  const hrefClicked = () => {
    if (rightSideButtonConfig?.href) {
      logPageAction(OnboardingPageAction.SwitchAccountLink);
      window.location.href = rightSideButtonConfig.href;
    }
  };
  return (
    <>
      {rightSideLinkConfig && (
        <Column className={styles.titleRightSideText}>
          <div className={styles.rightSideLinkWrapper}>
            <BasicButton
              as={"a"}
              buttonType={ButtonType.Primary}
              className={cx(styles.rightSideLink, colorStyle)}
              onClick={rightSideLinkConfig.onClick}
              onKeyDown={e => e.key === "Enter" && rightSideLinkConfig.onClick?.()}
              href={rightSideLinkConfig.href}
              tabIndex={0}
            >
              {rightSideLinkConfig.icon && (
                <div className={styles.rightSideLinkIconWrapper}>
                  {getIcon(rightSideLinkConfig.icon)}
                </div>
              )}
              {rightSideLinkConfig.text}
            </BasicButton>
          </div>
        </Column>
      )}
      {rightSideButtonConfig && (
        <Column className={styles.titleRightSideText}>
          <BasicButton
            as={"button"}
            buttonType={ButtonType.Primary}
            className={cx(styles.rightSideButton, colorStyle)}
            onClick={rightSideButtonConfig.onClick ?? hrefClicked()}
            role="link"
          >
            {rightSideButtonConfig.icon && (
              <div className={styles.rightSideLinkIconWrapper}>
                {getIcon(rightSideButtonConfig.icon)}
              </div>
            )}
            {rightSideButtonConfig.text}
          </BasicButton>
        </Column>
      )}
      {titleConfiguration.rightSideText && (
        <Column className={styles.titleRightSideText}>
          <p className={styles.rightSideTextWrapper}>
            <small>{titleConfiguration.rightSideText}</small>
          </p>
        </Column>
      )}
    </>
  );
};

export const ContentBox = ({ className, children, titleConfiguration }: ContentBoxProps) => {
  const currentViewPort = useCurrentViewPort();
  const isSmallView = currentViewPort <= ViewPort.SM;
  return (
    <Container
      key={titleConfiguration ? titleConfiguration.title : ""}
      className={cx(
        styles.contentBox,
        className,
        titleConfiguration && titleConfiguration.disabled ? styles.disabledContentBox : ""
      )}
    >
      {titleConfiguration && (
        <>
          <Row className={styles.containerRow}>
            <Column>
              <Row
                className={
                  titleConfiguration.icon && getIcon(titleConfiguration.icon)
                    ? ""
                    : styles.containerEmptyIcon
                }
              >
                {titleConfiguration.icon && (
                  <div className={styles.iconWrapper}>{getIcon(titleConfiguration.icon)}</div>
                )}
                <div>
                  <h2
                    className={cx(
                      styles.title,
                      titleConfiguration.disabled ? styles.disabledText : ""
                    )}
                  >
                    {titleConfiguration.title}
                  </h2>
                  {titleConfiguration.subtitle && (
                    <h3 className={styles.subtitle}>{titleConfiguration.subtitle}</h3>
                  )}
                </div>
              </Row>
              {isSmallView && (
                <Row>
                  <RightSide titleConfiguration={titleConfiguration} />
                </Row>
              )}
            </Column>
            {!isSmallView && <RightSide titleConfiguration={titleConfiguration} />}
          </Row>
          {titleConfiguration.separatorBar && <SeparatorBar />}
        </>
      )}
      {children}
    </Container>
  );
};
